import type { GsEvents } from 'GS/Events';

export interface EventBusRegistry {
  unsubscribe: () => void;
}

export type EventName = keyof GsEvents;

export type EventCallback<K extends EventName> = (arg: GsEvents[K]) => void;

type EventArgs<K extends EventName> = GsEvents[K] extends void
  ? []
  : [arg: GsEvents[K]];

export type Callable = {
  [K in EventName]?: {
    [key: string]: EventCallback<K>;
  };
};

export interface IEventBus {
  dispatch<K extends EventName>(event: K, ...args: EventArgs<K>): void;
  subscribe<K extends EventName>(
    event: K,
    callback: EventCallback<K>
  ): EventBusRegistry;
}

class EventBus implements IEventBus {
  private readonly subscribers: Callable;
  private static nextId = 0;
  private static instance?: EventBus = undefined;

  private constructor() {
    this.subscribers = {};
  }

  public static getInstance(): EventBus {
    if (!this.instance) {
      this.instance = new EventBus();
    }

    return this.instance;
  }

  public dispatch<K extends EventName>(event: K, ...args: EventArgs<K>): void {
    const eventSubscribers = this.subscribers[event];

    if (eventSubscribers === undefined) {
      return;
    }

    const payload = args[0];
    const callbacks = Object.values(eventSubscribers) as EventCallback<K>[];

    callbacks.forEach((callback) => {
      try {
        // @ts-ignore
        callback(payload);
      } catch (error) {
        console.error(`Eventbus subscriber failed for event ${String(event)}:`, error);
      }
    });
  }

  public subscribe<K extends EventName>(
    event: K,
    callback: EventCallback<K>
  ): EventBusRegistry {
    const id = this.getNextId().toString();

    if (!this.subscribers[event]) {
      this.subscribers[event] = {};
    }

    const subscribers = this.subscribers[event] as {
      [id: string]: EventCallback<K>;
    };

    subscribers[id] = callback;

    return {
      unsubscribe: () => {
        delete this.subscribers[event]![id];

        if (Object.keys(this.subscribers[event]!).length === 0) {
          delete this.subscribers[event];
        }
      },
    };
  }

  private getNextId(): number {
    return EventBus.nextId++;
  }
}

export const eventBus = EventBus.getInstance();
